import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Mascots, MASCOT_META } from '@assets';
import type { MascotKey } from '@assets';

type Move = 'idle' | 'bob' | 'pop' | 'none';

export interface MascotProps {
  /** Which coding-dog art to show. Transparent cutouts/poses are safe on any surface. */
  name: MascotKey;
  /** Rendered width in px (height auto). */
  size?: number;
  /** Idle motion. `idle`/`bob` = a slow float loop; `pop` = a one-shot entrance pop. */
  move?: Move;
  className?: string;
  style?: React.CSSProperties;
  alt?: string;
}

/**
 * The animated coding-dog. Wraps `Mascots` from assets with a lifelike idle
 * motion (per user Q7 — "I need animation … so it looks like moving"). All
 * motion is gated by `prefers-reduced-motion`.
 */
export const Mascot: React.FC<MascotProps> = ({ name, size = 120, move = 'bob', className, style, alt }) => {
  const reduce = useReducedMotion();
  const src = Mascots[name];
  const meta = MASCOT_META[name];

  const animate =
    reduce || move === 'none'
      ? undefined
      : move === 'pop'
        ? { scale: [0.7, 1.06, 1], opacity: [0, 1, 1] }
        : { y: [0, -7, 0], rotate: [0, -1.2, 0] };

  const transition =
    reduce || move === 'none'
      ? undefined
      : move === 'pop'
        ? { duration: 0.5, ease: [0.34, 1.56, 0.64, 1] as [number, number, number, number] }
        : { duration: 2.6, repeat: Infinity, ease: 'easeInOut' as const };

  return (
    <motion.img
      src={src.lg}
      srcSet={`${src.sm} 512w, ${src.lg} 960w`}
      sizes={`${size}px`}
      alt={alt ?? ''}
      aria-hidden={alt ? undefined : true}
      width={size}
      className={className}
      style={{
        width: size,
        height: 'auto',
        objectFit: 'contain',
        display: 'block',
        // scenes carry their own baked background — round them so they read as a panel
        borderRadius: meta.kind === 'scene' ? 'var(--sp-radius-md)' : undefined,
        ...style,
      }}
      initial={reduce || move === 'none' ? false : move === 'pop' ? { scale: 0.7, opacity: 0 } : undefined}
      animate={animate}
      transition={transition}
      loading="lazy"
    />
  );
};
